import React from "react";
import {
  CircularProgress,
  Grid,
  Typography,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { useQuery } from "@apollo/react-hooks";
import gql from "graphql-tag";
import moment from "moment";
import { useForm, Controller, useWatch } from "react-hook-form";

import Table from "../../components/Table";
import { DatePicker } from "@material-ui/pickers";
import { Card, CardContent, CardActions } from "../../components/Card";
import Text from "../../components/Text";
import { useAlert } from "../../components/Alert";

const GET_FORECLOSURES_ERRORCOUNT = gql`
  query foreclosures($onlyCount: Boolean, $errorLog: Boolean) {
    foreclosures(onlyCount: $onlyCount, errorLog: $errorLog) {
      count
    }
  }
`;

const GET_FORECLOSURES_COUNT = gql`
  query foreclosures($onlyCount: Boolean) {
    foreclosures(onlyCount: $onlyCount) {
      count
    }
  }
`;

const GET_USERS = gql`
  query users($identity: Identity) {
    users(identity: $identity) {
      contents {
        id
        fullName
      }
    }
  }
`;

const GET_EDITPROPERTYRECORDS = gql`
  query editPropertyRecords(
    $editorId: Int
    $startDate: Date
    $endDate: Date
    $latest: Boolean
    $pageSize: Int
    $page: Int
  ) {
    editPropertyRecords(
      editorId: $editorId
      startDate: $startDate
      endDate: $endDate
      latest: $latest
      pageSize: $pageSize
      page: $page
    ) {
      count
      pageCount
      contents {
        id
        editLog
        editProperty
        editor {
          id
          fullName
        }
        createdAt
      }
    }
  }
`;

export default function ObjEditRecordTable() {
  const { control, setValue } = useForm({
    defaultValues: {
      startDate: null,
      endDate: null,
      editorId: "",
      staffArray: [],
      page: 1,
    },
  });
  const page = useWatch({ name: "page", control });
  const editorId = useWatch({ name: "editorId", control });
  const startDate = useWatch({ name: "startDate", control });
  const endDate = useWatch({ name: "endDate", control });
  const staffArray = useWatch({ name: "staffArray", control });
  const Alert = useAlert();

  const { data, loading } = useQuery(GET_EDITPROPERTYRECORDS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: {
      editorId: editorId !== "" ? editorId : null,
      startDate,
      endDate,
      latest: true,
      pageSize: 10,
      page,
    },
    onError(error) {
      return Alert.notice(`${error}`);
    },
  });

  const { data: errorCountData, loading: errorCountLoading } = useQuery(
    GET_FORECLOSURES_ERRORCOUNT,
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      variables: {
        onlyCount: true,
        errorLog: true,
      },
    }
  );

  const { data: countData, loading: countLoading } = useQuery(
    GET_FORECLOSURES_COUNT,
    {
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
      variables: {
        onlyCount: true,
      },
    }
  );

  const { loading: usersLoading } = useQuery(GET_USERS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    variables: { identity: "STAFF" },
    onCompleted({ users }) {
      if (users) {
        let newArray = [];
        users.contents.forEach((item) => {
          newArray.push({ id: item.id, fullName: item.fullName });
        });
        setValue("staffArray", newArray);
      }
    },
    onError(error) {
      return Alert.notice(error.message.replace("GraphQL error: ", ""));
    },
  });
  if (loading || countLoading || errorCountLoading || usersLoading) {
    return (
      <Grid container justify="center">
        <CircularProgress color="secondary" />
      </Grid>
    );
  } else {
    return (
      <Card>
        <CardContent>
          <Grid container spacing={1}>
            <Grid item>
              <Typography>
                {`目前物件總筆數：${countData?.foreclosures.count}\u2002`}
              </Typography>
            </Grid>
            <Grid item>
              <Typography>
                {`剩餘錯誤物件總數：${errorCountData?.foreclosures.count}\u2002`}
              </Typography>
            </Grid>
            <Grid item>
              <Typography>
                {`${
                  editorId !== ""
                    ? "編輯者：" +
                      staffArray.find((item) => item.id === editorId).fullName
                    : "全部"
                }已修正物件總數：${data?.editPropertyRecords.count}`}
              </Typography>
            </Grid>
          </Grid>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={2}>
              <Controller
                name="editorId"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <>
                    <Typography color="primary" style={{ fontSize: 15 }}>
                      編輯者
                    </Typography>
                    <Select
                      {...field}
                      displayEmpty
                      fullWidth
                      variant="outlined"
                      style={{ height: "40px" }}
                    >
                      <MenuItem value="" selected>
                        全部
                      </MenuItem>
                      {staffArray.map((item) => (
                        <MenuItem value={item.id}>{item.fullName}</MenuItem>
                      ))}
                    </Select>
                  </>
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Controller
                name="startDate"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <>
                    <Typography color="primary" style={{ fontSize: 15 }}>
                      日期
                    </Typography>
                    <DatePicker
                      autoOk={true}
                      {...field}
                      format="yyyy-MM-dd"
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2}>
              <Controller
                name="endDate"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <>
                    <Typography color="primary" style={{ fontSize: 15 }}>
                      至
                    </Typography>
                    <DatePicker
                      autoOk={true}
                      {...field}
                      format="yyyy-MM-dd"
                      fullWidth
                    />
                  </>
                )}
              />
            </Grid>
          </Grid>
        </CardContent>
        <CardContent>
          <Table
            data={data?.editPropertyRecords.contents}
            tableWidth={2}
            header={["編號", "修正物件", "修訂者", "修訂時間", "修正訊息"]}
            columns={[
              (_, index) => <Text>{index + 1}</Text>,
              (item) => <Text>{item.editProperty}</Text>,
              (item) => <Text>{item.editor.fullName}</Text>,
              (item) => (
                <Text>{moment(item.createdAt).format("yyyy-MM-DD")}</Text>
              ),
              (item) => <Text>{item.editLog}</Text>,
            ]}
          />
        </CardContent>
        <CardActions>
          <Grid container justifyContent="center">
            <Pagination
              count={
                Boolean(data) && data.editPropertyRecords.pageCount
                  ? data.editPropertyRecords.pageCount
                  : 1
              }
              size="large"
              color="primary"
              page={page}
              onChange={(e, n) => {
                setValue("page", n);
              }}
            />
          </Grid>
        </CardActions>
      </Card>
    );
  }
}
